import { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '@/lib/api';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ProductIdentity } from '@/components/products';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ArrowLeft, Check, Package, XCircle } from 'lucide-react';

interface Warehouse {
  id: string;
  code: string;
  name: string;
  type: string;
}

interface TransferLine {
  id: string;
  productId: string;
  quantity: number;
  quantityReceived: number | null;
  batchNumber: string | null;
  batchExpiryDate: string | null;
  product?: { id: string; sku: string; name: string };
}

interface Transfer {
  id: string;
  transferNumber: string;
  transferDate: string;
  status: string;
  notes: string | null;
  fromWarehouse?: Warehouse;
  toWarehouse?: Warehouse;
  lines?: TransferLine[];
  totalItems?: number;
  totalQuantity?: number;
}

const statusStyles: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-800',
  APPROVED: 'bg-blue-100 text-blue-800',
  SHIPPED: 'bg-yellow-100 text-yellow-800',
  RECEIVED: 'bg-green-100 text-green-800',
  CANCELLED: 'bg-red-100 text-red-800',
};

function initialReceiveQty(lines: TransferLine[]): Record<string, string> {
  const map: Record<string, string> = {};
  for (const line of lines) {
    map[line.id] = String(line.quantity);
  }
  return map;
}

export default function TransferDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [transfer, setTransfer] = useState<Transfer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [receiveQty, setReceiveQty] = useState<Record<string, string>>({});
  const [receiveNotes, setReceiveNotes] = useState('');
  const [showReceiveForm, setShowReceiveForm] = useState(false);
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);

  const loadTransfer = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/v1/transfers/${id}`);
      if (!res.ok) throw new Error('Failed to load transfer');
      const data: Transfer = await res.json();
      setTransfer(data);
      setReceiveQty(initialReceiveQty(data.lines ?? []));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load transfer');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadTransfer();
  }, [loadTransfer]);

  const performAction = async (action: 'approve' | 'ship' | 'cancel') => {
    if (!id) return;
    setActionLoading(action);
    setError(null);
    try {
      const res = await apiFetch(`/api/v1/transfers/${id}/${action}`, {
        method: 'PATCH',
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error ?? `Failed to ${action}`);
      }
      setConfirmCancelOpen(false);
      await loadTransfer();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : `Failed to ${action}`);
    } finally {
      setActionLoading(null);
    }
  };

  const submitReceive = async () => {
    if (!id || !transfer?.lines?.length) return;

    setActionLoading('receive');
    setError(null);

    const lines: { lineId: string; quantityReceived: number }[] = [];

    for (const line of transfer.lines) {
      const raw = receiveQty[line.id]?.trim() ?? '';
      const quantityReceived = raw === '' ? NaN : parseInt(raw, 10);
      if (!Number.isInteger(quantityReceived) || quantityReceived < 0) {
        setError(`Enter a valid received quantity for ${line.product?.sku ?? line.productId}`);
        setActionLoading(null);
        return;
      }
      if (quantityReceived > line.quantity) {
        setError(
          `Received quantity cannot exceed shipped quantity (${line.quantity}) for ${line.product?.sku ?? line.productId}`
        );
        setActionLoading(null);
        return;
      }
      lines.push({ lineId: line.id, quantityReceived });
    }

    try {
      const body: { lines: typeof lines; notes?: string } = { lines };
      const notes = receiveNotes.trim();
      if (notes) body.notes = notes;

      const res = await apiFetch(`/api/v1/transfers/${id}/receive`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error ?? 'Failed to receive transfer');
      }
      setShowReceiveForm(false);
      await loadTransfer();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to receive transfer');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!transfer) {
    return (
      <div className="min-h-screen bg-muted/30">
        <div className="space-y-6">
          <div className="p-4 rounded-md bg-destructive/10 text-destructive">
            {error || 'Transfer not found'}
          </div>
          <Button variant="outline" onClick={() => navigate('/locations/transfers')}>
            Back to Transfers
          </Button>
        </div>
      </div>
    );
  }

  const canApprove = transfer.status === 'DRAFT';
  const canShip = transfer.status === 'APPROVED';
  const canReceive = transfer.status === 'SHIPPED';
  const canCancel =
    transfer.status === 'DRAFT' ||
    transfer.status === 'APPROVED' ||
    transfer.status === 'SHIPPED';

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/locations/transfers')}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-semibold">{transfer.transferNumber}</h1>
              <span
                className={`inline-block mt-1 px-2 py-0.5 text-xs rounded-full ${
                  statusStyles[transfer.status] || 'bg-muted'
                }`}
              >
                {transfer.status}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {canApprove && (
              <Button
                variant="outline"
                size="sm"
                disabled={!!actionLoading}
                onClick={() => performAction('approve')}
              >
                {actionLoading === 'approve' ? '...' : (
                  <>
                    <Check className="h-4 w-4 mr-1" />
                    Approve
                  </>
                )}
              </Button>
            )}
            {canShip && (
              <Button
                variant="outline"
                size="sm"
                disabled={!!actionLoading}
                onClick={() => performAction('ship')}
              >
                {actionLoading === 'ship' ? '...' : (
                  <>
                    <Package className="h-4 w-4 mr-1" />
                    Ship
                  </>
                )}
              </Button>
            )}
            {canReceive && !showReceiveForm && (
              <Button size="sm" disabled={!!actionLoading} onClick={() => setShowReceiveForm(true)}>
                <Check className="h-4 w-4 mr-1" />
                Confirm receipt
              </Button>
            )}
            {canCancel && (
              <Button
                variant="destructive"
                size="sm"
                disabled={!!actionLoading}
                onClick={() => {
                  if (transfer.status === 'SHIPPED') {
                    setConfirmCancelOpen(true);
                    return;
                  }
                  void performAction('cancel');
                }}
              >
                {actionLoading === 'cancel' ? '...' : (
                  <>
                    <XCircle className="h-4 w-4 mr-1" />
                    {transfer.status === 'SHIPPED' ? 'Cancel shipment' : 'Cancel'}
                  </>
                )}
              </Button>
            )}
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-md bg-destructive/10 text-destructive text-sm">
            {error}
          </div>
        )}

        <Dialog
          open={confirmCancelOpen}
          onOpenChange={(open) => actionLoading !== 'cancel' && setConfirmCancelOpen(open)}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Cancel this shipment?</DialogTitle>
              <DialogDescription>
                Reserved hub stock is released back to available. The destination will not receive
                these items. This cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                disabled={actionLoading === 'cancel'}
                onClick={() => setConfirmCancelOpen(false)}
              >
                Keep shipment
              </Button>
              <Button
                type="button"
                variant="destructive"
                disabled={actionLoading === 'cancel'}
                onClick={() => void performAction('cancel')}
              >
                {actionLoading === 'cancel' ? 'Cancelling…' : 'Cancel shipment'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {canReceive && showReceiveForm && (
          <Card className="border-primary/30">
            <CardHeader>
              <CardTitle>Confirm received quantities</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Enter what actually arrived at {transfer.toWarehouse?.name ?? 'the spoke'}. Any
                short-ship will be written off at the hub.
              </p>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead className="hidden sm:table-cell">Batch</TableHead>
                    <TableHead className="text-right">Shipped</TableHead>
                    <TableHead className="text-right w-32">Received</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(transfer.lines ?? []).map((line) => (
                    <TableRow key={line.id}>
                      <TableCell>
                        <ProductIdentity
                          productId={line.productId}
                          name={line.product?.name ?? line.productId}
                          sku={line.product?.sku}
                          size="sm"
                        />
                      </TableCell>
                      <TableCell className="hidden font-mono text-sm sm:table-cell">{line.batchNumber ?? '—'}</TableCell>
                      <TableCell className="text-right">{line.quantity}</TableCell>
                      <TableCell className="text-right">
                        <Input
                          type="number"
                          min={0}
                          max={line.quantity}
                          className="text-right h-9"
                          value={receiveQty[line.id] ?? ''}
                          onChange={(e) =>
                            setReceiveQty((prev) => ({ ...prev, [line.id]: e.target.value }))
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div>
                <Label htmlFor="receiveNotes">Receipt notes (optional)</Label>
                <Textarea
                  id="receiveNotes"
                  className="mt-1"
                  placeholder="Damage, missing cases, etc."
                  value={receiveNotes}
                  onChange={(e) => setReceiveNotes(e.target.value)}
                  rows={3}
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  disabled={actionLoading === 'receive'}
                  onClick={() => {
                    setShowReceiveForm(false);
                    setReceiveNotes('');
                    setReceiveQty(initialReceiveQty(transfer.lines ?? []));
                  }}
                >
                  Cancel
                </Button>
                <Button disabled={actionLoading === 'receive'} onClick={() => void submitReceive()}>
                  {actionLoading === 'receive' ? 'Saving…' : 'Confirm receipt'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Transfer Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">From</p>
                <p className="font-medium">
                  {transfer.fromWarehouse?.name || '-'} ({transfer.fromWarehouse?.code})
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">To</p>
                <p className="font-medium">
                  {transfer.toWarehouse?.name || '-'} ({transfer.toWarehouse?.code})
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Date</p>
                <p className="font-medium">
                  {new Date(transfer.transferDate).toLocaleDateString()}
                </p>
              </div>
              {transfer.notes && (
                <div className="col-span-2">
                  <p className="text-sm text-muted-foreground">Notes</p>
                  <p className="text-sm">{transfer.notes}</p>
                </div>
              )}
            </div>

            <div>
              <p className="text-sm text-muted-foreground mb-2">Lines</p>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead className="hidden sm:table-cell">Batch</TableHead>
                    <TableHead className="text-right">Shipped</TableHead>
                    <TableHead className="text-right">Received</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(transfer.lines ?? []).map((line) => (
                    <TableRow key={line.id}>
                      <TableCell>
                        <ProductIdentity
                          productId={line.productId}
                          name={line.product?.name ?? line.productId}
                          sku={line.product?.sku}
                          size="sm"
                        />
                      </TableCell>
                      <TableCell className="hidden font-mono text-sm sm:table-cell">
                        {line.batchNumber ?? '—'}
                        {line.batchExpiryDate && (
                          <span className="block text-xs text-muted-foreground font-sans">
                            Exp {new Date(line.batchExpiryDate).toLocaleDateString('en-GB')}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">{line.quantity}</TableCell>
                      <TableCell className="text-right">
                        {line.quantityReceived ?? '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
